// services/internship.service.ts

export type InternshipStatus = "active" | "pending" | "completed";
export type InternshipMode = "On-site" | "Remote" | "Hybrid";

export interface InternshipType {
  _id: string;
  position: string; // e.g. "Software Development Intern"
  company: string; // Vendor ID
  students: string[]; // Array of Student IDs
  departments: string[]; // Array of Dept IDs/Names
  batches: string[]; // Array of Batch IDs
  duration: string; // e.g. "3 months"
  stipend: number; // monthly stipend in USD, e.g. 2500
  mode: InternshipMode;
  startDate: string; // ISO date string
  endDate?: string; // ISO date string
  status: InternshipStatus;
  description?: string;
  requirements?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InternshipCreateType {
  position: string;
  company: string;
  students: string[];
  departments: string[];
  batches: string[];
  duration: string;
  stipend: number;
  mode: InternshipMode;
  startDate: string;
  endDate?: string;
  status: InternshipStatus;
  description?: string;
  requirements?: string;
}

export interface InternshipMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type InternshipParamsType = {
  search?: string;
  status?: InternshipStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_INTERNSHIPS: InternshipType[] = [
  {
    _id: "1",
    position: "Software Development Intern",
    company: "1", // TechCorp Solutions
    students: ["1", "4"], // Alex Johnson, Sophia Brown
    departments: ["CSE"],
    batches: ["1"], // CSE 2021-2025
    duration: "3 months",
    stipend: 2500,
    mode: "On-site",
    startDate: "2025-06-01",
    endDate: "2025-09-01",
    status: "active",
    description: "Hands-on software development experience.",
    requirements: "Proficient in React and Node.js",
    createdAt: "2025-05-01T00:00:00.000Z",
    updatedAt: "2025-05-01T00:00:00.000Z",
  },
  {
    _id: "2",
    position: "Data Science Intern",
    company: "2", // InnovateLabs
    students: ["2"], // Emma Watson
    departments: ["IT"],
    batches: ["3"], // IT 2021-2025
    duration: "6 months",
    stipend: 2800,
    mode: "Hybrid",
    startDate: "2025-05-15",
    endDate: "2025-11-15",
    status: "active",
    description: "Working on AI models for healthcare.",
    requirements: "Python and ML basics",
    createdAt: "2025-04-15T00:00:00.000Z",
    updatedAt: "2025-04-15T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  records: InternshipType[],
  filters: InternshipParamsType
): { data: InternshipType[]; total: number } => {
  let result = [...records];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (r) =>
        r.position.toLowerCase().includes(q) ||
        // r.company.toLowerCase().includes(q) || // Needs lookup if searching by company name
        r.description?.toLowerCase().includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((r) => r.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllInternships = async (filters: InternshipParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/internships/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_INTERNSHIPS, filters);
  return {
    internships: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as InternshipMetaType,
  };
};

export const fetchInternshipById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/internships/${_id}`);
  const record = DUMMY_INTERNSHIPS.find((r) => r._id === _id);
  if (!record) throw new Error("Internship not found");
  return record;
};

export const createInternship = async (data: InternshipCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/internships/", data);
  const newRecord: InternshipType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_INTERNSHIPS.push(newRecord);
  return newRecord;
};

export const updateInternship = async (
  _id: string,
  data: InternshipCreateType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/internships/${_id}`, data);
  const index = DUMMY_INTERNSHIPS.findIndex((r) => r._id === _id);
  if (index === -1) throw new Error("Internship not found");
  DUMMY_INTERNSHIPS[index] = {
    ...DUMMY_INTERNSHIPS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_INTERNSHIPS[index];
};

export const deleteInternship = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/internships/${_id}`);
  const index = DUMMY_INTERNSHIPS.findIndex((r) => r._id === _id);
  if (index === -1) throw new Error("Internship not found");
  const [deleted] = DUMMY_INTERNSHIPS.splice(index, 1);
  return deleted;
};
